import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ImageGallery from "../../components/ProductManagement/ImageGallery";
import { categoryApi, productApi } from "../../services/productApi";
import Heading2 from "../../components/Basic/Heading2";
import ToggleField from "../../components/ProductManagement/ToggleField";
import Button from "../../components/Button";
import { productReviewAdminApi } from '../../services/productReviewAdminApi';

export default function ProductInspection() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categoryAttributes, setCategoryAttributes] = useState([]);
  const [oldProductData, setOldProductData] = useState({
    name: '',
    brand: '',
    description: '',
    unit_of_measure: "count",
  });

  const [productData, setProductData] = useState({
    name: '',
    brand: '',
    category: null,
    category_name: '',
    description: '',
    price: '',
    stock: '',
    unit_of_measure: "count",
    status: '',
    attributes: {},
    custom_attributes: [],
    product_code: '',
    is_packable: true,
    is_fragile: false,
    is_flammable: false,
    is_liquid: false,
    is_corrosive: false,
    width: 0,
    length: 0,
    height: 0,
    weight: 0,
    delivery_by_supplier: true,
    delivery_by_customer: true,
    delivery_by_carrier: true,
    images: [],
    featured_image: null,
  });

  const [inspectionData, setInspectionData] = useState({
    is_approved: false,
    inspector_notes: '',
    edited_fields: {},
  });
  const [reviewRes, setReviewRes] = useState({
    comment: '',
    reviews_count: 0,
  });

  const UNIT_OPTIONS = [
    { value: 'count', label: 'تعداد' },
    { value: 'package', label: 'بسته' },
    { value: 'weight', label: 'کیلو گرم' },
    { value: 'length', label: 'متر' },
    { value: 'area', label: 'متر مربع' },
    { value: 'litr', label: 'لیتر' },
    { value: 'volume', label: 'متر مکعب' }
  ];

  useEffect(() => {
    const loadProduct = async () => {
      if (!id) return;

      try {
        setLoading(true);

        // ۱. بارگذاری اطلاعات محصول با استفاده از سرویس API کالا
        const productResponse = await productReviewAdminApi.get(id);
        const product = productResponse.data || productResponse;

        // ۲. بارگذاری آخرین تاریخچه بازرسی از طریق سرویس بازرسی ادمین
        try {
          const revRes = await productReviewAdminApi.getLastReview(id);
          setReviewRes({
            comment: revRes.comment || '',
            reviews_count: revRes.reviews_count || 0,
          });
        } catch (reviewErr) {
          console.error("خطا در دریافت آخرین بازرسی:", reviewErr);
        }

        // ۳. تبدیل مشخصه‌های سیستمی
        const systemAttributes = {};
        if (product.attribute_values && Array.isArray(product.attribute_values)) {
          product.attribute_values.forEach(attr => {
            const attributeId = attr.attribute_id;
            if (attributeId) {
              systemAttributes[attributeId] = {
                id: attributeId,
                name: attr.title || '',
                value: attr.value || '',
                is_featured: attr.is_featured || false
              };
            }
          });
        }

        // ۴. مشخصه‌های سفارشی
        const customAttributes = (product.custom_attributes || []).map(attr => ({
          id: attr.id,
          uuid: attr.uuid || crypto.randomUUID(),
          title: attr.title || "",
          value: attr.value || "",
          unit: attr.unit || "",
          is_featured: attr.is_featured || false
        }));

        const formattedProduct = {
          ...product,
          attributes: systemAttributes,
          custom_attributes: customAttributes,
          images: product.images || [],
          featured_image: product.featured_image || null,
        };

        setProductData(formattedProduct);

        // ذخیره مقادیر اولیه برای تشخیص تغییرات انجام شده توسط ادمین بازرس
        const OldValueEditableFields = {
          name: product.name || '',
          brand: product.brand || '',
          description: product.description || '',
          unit_of_measure: product.unit_of_measure || 'count',
        };
        setOldProductData(OldValueEditableFields);

        // ۵. بارگذاری مشخصه‌های دسته
        if (formattedProduct.category) {
          try {
            const attrResponse = await categoryApi.getAttributes(formattedProduct.category);
            const attributesWithValues = (attrResponse.data || []).map(attr => ({
              ...attr,
              name: attr.attribute.name,
              current_value: systemAttributes[attr.id]?.value || ''
            }));
            setCategoryAttributes(attributesWithValues);
          } catch (error) {
            console.error("خطا در بارگذاری مشخصه‌های دسته:", error);
          }
        }

      } catch (err) {
        console.error("خطا در بارگذاری محصول:", err);
        alert("خطا در بارگذاری محصول");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, navigate]);

  // توابع تغییر مقادیر فرم
  const handleFieldChange = (field, value) => {
    setProductData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleReviewFieldChange = (fieldKey, value) => {
    setInspectionData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const EDITABLE_FIELDS = ['name', 'brand', 'description', 'unit_of_measure'];

  // مقایسه مقادیر اولیه با مقادیر ویرایش شده جدید
  const buildEditedFields = (original, current) => {
    const edited = {};
    EDITABLE_FIELDS.forEach(field => {
      if (original[field] !== current[field]) {
        edited[field] = current[field];
      }
    });
    return edited;
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // مقایسه مقادیر تغییر یافته با مقادیر ذخیره شده اولیه کالا
      const currentFormValues = {
        name: productData.name,
        brand: productData.brand,
        description: productData.description,
        unit_of_measure: productData.unit_of_measure,
      };

      const editedFields = buildEditedFields(oldProductData, currentFormValues);

      const reviewData = {
        decision: inspectionData.is_approved ? 'approve' : 'request_edit',
        comment: inspectionData.inspector_notes,
        edited_fields: editedFields
      };

      console.log('ارسال داده بازرسی:', reviewData);

      // ارسال داده‌ها به آدرس /products/product-review/{id}/submit/
      await productReviewAdminApi.submitReview(id, reviewData);

      alert('نتیجه بازرسی با موفقیت ثبت شد.');
      navigate('/admin-panel/content-inspection-management/');
    } catch (err) {
      console.error('خطا در ارسال نتیجه بازرسی:', err);
      const errMsg = err.response?.data?.detail || err.message || 'خطای غیرمنتظره';
      alert('خطا در ثبت نتیجه بازرسی: ' + errMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm("آیا از انصراف از بازرسی اطمینان دارید؟ تغییرات ذخیره نخواهد شد.")) {
      navigate('/admin-panel/content-inspection-management/');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">در حال بارگذاری محصول...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* هدر */}
      <div className="mb-6 pb-4 border-b">
        <h1 className="text-2xl font-bold text-gray-800">بازرسی محصول</h1>
        <p className="text-gray-600 mt-1">
          در حال بازرسی: <span className="font-semibold">{productData.name}</span>
          <span className="mr-4"> - کد محصول: {productData.product_code || ''}</span>
        </p>
        {reviewRes.reviews_count > 0 && (
          <p className='my-[8px] text-[16px] text-blue-600 font-medium'>تعداد مرتبه بازرسی: {reviewRes.reviews_count}</p>
        )}
      </div>

      {/* ---------- اطلاعات پایه ---------- */}
      <section className="bg-white rounded-lg shadow-sm p-6 border mb-6">
        <Heading2 text={'اطلاعات پایه محصول'} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* نام محصول */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              نام محصول *
            </label>
            <input
              type="text"
              value={productData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* برند */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              برند
            </label>
            <input
              type="text"
              value={productData.brand || ''}
              onChange={(e) => handleFieldChange('brand', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* دسته‌بندی */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              دسته‌بندی
            </label>
            <div className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600">
              {productData.category_name}
            </div>
            <p className="text-xs text-gray-500 mt-1">دسته‌بندی قابل ویرایش نیست</p>
          </div>

          {/* واحد اندازه‌گیری */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              واحد اندازه‌گیری *
            </label>
            <select
              value={productData.unit_of_measure}
              onChange={(e) => handleFieldChange('unit_of_measure', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {UNIT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* مشخصات فیزیکی */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              عرض (سانتی متر)
            </label>
            <div className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600">
              {productData.width ? Number(productData.width) : '0'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              طول (سانتی متر)
            </label>
            <div className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600">
              {productData.length ? Number(productData.length) : '0'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ارتفاع (سانتی متر)
            </label>
            <div className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600">
              {productData.height ? Number(productData.height) : '0'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              وزن (کیلوگرم)
            </label>
            <div className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600">
              {productData.weight ? Number(productData.weight) : '0'}
            </div>
          </div>
        </div>
      </section>

      {/* تصاویر و توضیحات */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <section className="bg-white rounded-lg shadow-sm p-6 border">
          <Heading2 text={'تصاویر محصول'} />
          <div className="text-sm text-gray-500 mb-4">
            <span className="inline-flex items-center">
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              تصاویر قابل ویرایش نیستند
            </span>
          </div>
          <div className="aspect-square">
            <ImageGallery
              images={productData.images || []}
              productName={productData.name}
            />
          </div>
        </section>

        <section className="bg-white rounded-lg shadow-sm p-6 border">
          <Heading2 text={'توضیحات محصول'} />
          <div className="h-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              توضیحات
            </label>
            <textarea
              value={productData.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              rows={12}
              className="w-full h-[calc(100%-40px)] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="توضیحات محصول را وارد کنید..."
            />
          </div>
        </section>
      </div>

      {/* مشخصات سیستمی */}
      {categoryAttributes.length > 0 && (
        <section className="bg-white rounded-lg shadow-sm p-6 border mb-6">
          <Heading2 text={'مشخصه‌های سیستمی'} />
          <div className="text-sm text-gray-500 mb-4">
            مشخصه‌های تعریف شده برای دسته‌بندی محصول
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categoryAttributes.map(attr => {
              const attributeValue = attr.current_value || '';
              return (
                <div key={attr.id} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {attr.name}
                    {attr.unit && (<span className="text-gray-700 mr-1"> ({attr.unit}) </span>)}
                    {productData.attributes[attr.attribute.id]?.is_featured &&
                      <span className="text-blue-500 mr-1"> (شاخص)</span>}
                  </label>
                  <div className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600">
                    {attributeValue}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* مشخصه‌های سفارشی */}
      {productData.custom_attributes.length > 0 && (
        <section className="bg-white rounded-lg shadow-sm p-6 border mb-6">
          <Heading2 text={'مشخصه‌های سفارشی'} />
          <div className="text-sm text-gray-500 mb-4">
            مقادیر مشخصه‌های سفارشی غیرقابل ویرایش مستقیم در بازرسی هستند
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {productData.custom_attributes.map((attr) => (
              <div key={attr.id || attr.uuid} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {attr.title}
                  {attr.unit && (<span className="text-gray-700 mr-1"> ({attr.unit}) </span>)}
                  {attr.is_featured &&
                    <span className="text-blue-500 mr-1"> (شاخص)</span>}
                </label>
                <div className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600">
                  {attr.value}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ویژگی‌ها و روش‌های تحویل */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <section className="bg-white rounded-lg shadow-sm p-6 border">
          <Heading2 text={'ویژگی‌های محصول'} />
          <div className="space-y-3">
            {[
              { label: 'قابل بسته‌بندی', val: productData.is_packable, color: 'bg-green-100 text-green-800' },
              { label: 'شکننده', val: productData.is_fragile, color: 'bg-red-100 text-red-800' },
              { label: 'قابل اشتعال', val: productData.is_flammable, color: 'bg-red-100 text-red-800' },
              { label: 'مایع', val: productData.is_liquid, color: 'bg-blue-100 text-blue-800' },
              { label: 'خورنده', val: productData.is_corrosive, color: 'bg-red-100 text-red-800' }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <span className="text-gray-700">{item.label}</span>
                <span className={`px-3 py-1 rounded-full text-sm ${item.val ? item.color : 'bg-gray-100 text-gray-800'}`}>
                  {item.val ? 'بله' : 'خیر'}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-lg shadow-sm p-6 border">
          <Heading2 text={'روش‌های تحویل'} />
          <div className="space-y-3">
            {[
              { label: 'تحویل توسط فروشنده', val: productData.delivery_by_supplier },
              { label: 'تحویل توسط خریدار', val: productData.delivery_by_customer },
              { label: 'تحویل توسط پیک', val: productData.delivery_by_carrier }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <span className="text-gray-700">{item.label}</span>
                <span className={`px-3 py-1 rounded-full text-sm ${item.val ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {item.val ? 'فعال' : 'غیرفعال'}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ---------- نتیجه بازرسی ---------- */}
      <section className="bg-white rounded-lg shadow-sm p-6 border">
        <Heading2 text={'نتیجه بازرسی'} />

        <div className="mb-6">
          <ToggleField
            title={'تایید محصول'}
            checked={inspectionData.is_approved}
            fieldKey="is_approved"
            onChange={handleReviewFieldChange}
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            نظرات کارشناس محتوی
          </label>
          <textarea
            value={inspectionData.inspector_notes}
            onChange={(e) => handleReviewFieldChange('inspector_notes', e.target.value)}
            rows={4}
            placeholder="نظرات، اصلاحات و پیشنهادات خود را وارد کنید..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex gap-3 flex-wrap">
          <Button
            variant={'primary'}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'در حال ثبت...' : 'ثبت نتیجه بازرسی'}
          </Button>
          <Button
            variant={'secondary'}
            onClick={handleCancel}
          >
            انصراف
          </Button>
        </div>
      </section>
    </div>
  );
}
